import React, { useEffect, useRef, useState } from "react";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  Box,
  Button,
  Link,
  MenuItem,
  Select,
  TextField,
  Typography,
  Tab,
  InputLabel,
  FormControl,
} from "@mui/material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { updateSnackbar } from "../../../_redux/SnackbarSlice";
import { updateLoader } from "../../../_redux/LoaderSlice";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getApi, postApi } from "../../../_redux/_apiHelpers/_apihelper";
import { GetUserId, isIntegrationActive } from "../../../_utilities/getUserInfo";
import { extractFileContent, parseJD, normalizeToAIShape } from "../../../_utilities/clientJdParser";
import { parseSlots, fillSlots, hasSlots } from "./parseSlots";
import "./UploadJDPanel.css";
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

/* -------------------- API calls -------------------- */
async function parseWithAI({ file, text }) {
  const formData = new FormData();
  if (file) {
    formData.append("JD", file);
  } else {
    formData.append("text", text);
  }

  const response = await postApi(
    '/mgmt/jobopening/parseJD',
    formData,
    process.env.REACT_APP_JOBOPENING,
    true // isFormData
  );

  if (!response.data?.success) {
    throw new Error(response.data?.error || "Failed to parse job description.");
  }

  return { ...response.data.data, rawText: response.data.rawText };
}

async function createJDWithAI({ templateInputs }) {
  const response = await postApi(
    '/mgmt/jobopening/createJD',
    templateInputs,
    process.env.REACT_APP_JOBOPENING
  );

  if (!response.data?.success) {
    throw new Error(response.data?.error || "Failed to create job description.");
  }

  return { ...response.data.data, rawText: response.data.rawText };
}

const INITIAL_TEMPLATE = {
  jobTitle: "",
  minExperience: "",
  maxExperience: "",
  primarySkills: "",
  secondarySkills: "",
  workModel: "",
  location: "",
  jobType: "",
  education: "",
  industry: "",
  companyName: "",
  additionalNotes: "",
};

const UploadJDPanel = ({ onReceivechildProps }) => {
  const [tabValue, setTabValue] = useState("1");
  const [file, setFile] = useState(null);
  const [pasteText, setPasteText] = useState("");
  const [error, setError] = useState("");
  const [isParsing, setIsParsing] = useState(false);
  const [templateInputs, setTemplateInputs] = useState({ ...INITIAL_TEMPLATE });
  const [jdTemplates, setJdTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [slotValues, setSlotValues] = useState({});
  const [templatesLoaded, setTemplatesLoaded] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const isAIEnabled = isIntegrationActive("OpenAI");

  const customerId = GetUserId();
  const selectedTemplate = jdTemplates.find((t) => t.id === selectedTemplateId) || null;

  useEffect(() => {
    if (tabValue !== "3" || templatesLoaded || !customerId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await getApi(
          `/mgmt/customer/${customerId}/jdtemplates`,
          process.env.REACT_APP_USE_MANAGEMENT,
        );
        const list = Array.isArray(res?.data?.jdtemplates)
          ? res.data.jdtemplates
          : Array.isArray(res?.data?.data)
            ? res.data.data
            : Array.isArray(res?.data)
              ? res.data
              : [];
        if (!cancelled) {
          setJdTemplates(list);
          setTemplatesLoaded(true);
        }
      } catch (err) {
        if (!cancelled) setTemplatesLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tabValue, templatesLoaded, customerId]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setError("");
    setFile(null);
    setPasteText("");
    setTemplateInputs({ ...INITIAL_TEMPLATE });
    setSelectedTemplateId("");
    setSlotValues({});
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleTemplatePick = (e) => {
    setSelectedTemplateId(e.target.value);
    setSlotValues({});
    setError("");
  };

  const handleSlotChange = (label) => (e) => {
    setSlotValues((prev) => ({ ...prev, [label]: e.target.value }));
  };

  const handleCreateJDFromTemplate = async () => {
    if (!selectedTemplate?.body) {
      setError("Please select a template.");
      return;
    }
    const hasFilled = Object.values(slotValues || {}).some(
      (v) => String(v || "").trim().length > 0
    );
    if (!hasFilled) {
      setError("Please fill in at least one template field before creating the JD.");
      return;
    }
    const filled = fillSlots(selectedTemplate.body, slotValues);
    const templateText = filled
      .replace(/\[\[[^\]]+\]\][,.;:]?\s*/g, " ")
      .replace(/[ \t]+/g, " ")
      .replace(/ *\n */g, "\n")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
    if (templateText.length > 50000) {
      setError("Filled template is too long (max 50,000 characters). Please shorten it.");
      return;
    }
    const payload = {
      templateText,
      additionalNotes: templateInputs.additionalNotes?.trim() || "",
    };
    try {
      setError("");
      setIsParsing(true);
      dispatch(updateLoader(true));
      const parsed = await createJDWithAI({ templateInputs: payload });
      handleParseResult(parsed);
    } catch (err) {
      handleError(err);
    } finally {
      setIsParsing(false);
      dispatch(updateLoader(false));
    }
  };

  const handleParseResult = (parsed) => {
    navigate('/ats/layout/jobpostings/create/jobdetails', {
      state: { parsedData: parsed }
    });
    onReceivechildProps(false, "close");
  };

  const handleError = (err) => {
    if (err?.name === "CanceledError" || err?.name === "AbortError") return;

    const message =
      err?.response?.data?.error ||
      err?.message ||
      "Failed to parse job description.";

    dispatch(updateSnackbar({ type: "error", message }));
    setError(message);
  };

  const handleFileChange = async (event) => {
    const uploadedFile = event.target.files?.[0];
    if (!uploadedFile) return;

    if (uploadedFile.size > 10 * 1024 * 1024) {
      dispatch(
        updateSnackbar({ type: "error", message: "File too large (max 10MB)." }),
      );
      setError("File too large (max 10MB).");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    const ext = uploadedFile.name.split(".").pop()?.toLowerCase();
    if (!["pdf", "docx", "txt"].includes(ext || "")) {
      dispatch(
        updateSnackbar({
          type: "error",
          message: "Only PDF, DOCX, TXT are supported.",
        }),
      );
      setError("Only PDF, DOCX, TXT are supported.");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    setError("");
    setFile(uploadedFile);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleParse = async (sourceFile, sourceText) => {
    try {
      setError("");
      setIsParsing(true);
      dispatch(updateLoader(true));

      const parsed = await parseWithAI({
        file: sourceFile || undefined,
        text: sourceText || undefined,
      });

      handleParseResult(parsed);
    } catch (err) {
      handleError(err);
    } finally {
      setIsParsing(false);
      dispatch(updateLoader(false));
    }
  };

  const handleClientSideParse = async (sourceFile, sourceText) => {
    try {
      setError("");
      setIsParsing(true);
      dispatch(updateLoader(true));

      const rawText = sourceFile
        ? await extractFileContent(sourceFile)
        : sourceText;
      const parsedSections = parseJD(rawText);
      const normalized = normalizeToAIShape(parsedSections, rawText);
      handleParseResult(normalized);
    } catch (err) {
      handleError(err);
    } finally {
      setIsParsing(false);
      dispatch(updateLoader(false));
    }
  };

  const handlePasteSubmit = () => {
    const trimmed = pasteText.trim();
    if (!trimmed) {
      setError("Please paste a job description first.");
      return;
    }
    if (trimmed.length < 20) {
      setError("The text is too short to parse as a job description.");
      return;
    }
    if (isAIEnabled) {
      handleParse(null, trimmed);
    } else {
      handleClientSideParse(null, trimmed);
    }
  };

  const handleTemplateChange = (field) => (e) => {
    setTemplateInputs((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleCreateJD = async () => {
    const hasFilled = Object.entries(templateInputs).some(
      ([, v]) => String(v || "").trim().length > 0,
    );
    if (!hasFilled) {
      setError("Please fill in at least one field before creating the JD.");
      return;
    }

    try {
      setError("");
      setIsParsing(true);
      dispatch(updateLoader(true));

      const parsed = await createJDWithAI({ templateInputs });

      handleParseResult(parsed);
    } catch (err) {
      handleError(err);
    } finally {
      setIsParsing(false);
      dispatch(updateLoader(false));
    }
  };

  /* ---------- shared button row (matches ApplicantsRenderData pattern) ---------- */
  const renderButtons = (label, onClick) => (
    <div style={{ textAlign: "right", marginTop: "8%" }}>
      <Button
        variant="text"
        type="button"
        className="addApplicantcancelBtn"
        onClick={() => onReceivechildProps(false, "close")}
        size="small"
      >
        <Link
          href="#"
          style={{ color: "red", fontSize: "14px" }}
          underline="always"
        >
          Cancel
        </Link>
      </Button>
      <Button
        type="button"
        variant="contained"
        size="small"
        className="saveBtn"
        onClick={onClick}
      >
        {label}
      </Button>
    </div>
  );

  return (
    <div className="tabs">
      <Box
        sx={{
          width: "100%",
          typography: "body1",
          color: "#3B4046",
          marginTop: "-35px",
        }}
      >
        <TabContext value={tabValue}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <TabList
              onChange={handleTabChange}
              aria-label="Upload JD tabs"
              className="tabsvalue"
            >
              <Tab label="Upload JD File" value="1" />
              <Tab label="Paste JD Text" value="2" />
              {isAIEnabled && <Tab label="Create JD" value="3" />}
            </TabList>
          </Box>

          {/* Upload File Tab */}
          <TabPanel value="1">
            {/* <div className="beta-warning">
              <WarningAmberIcon /><b>This is a beta release, please verify all results to ensure accuracy.</b>
            </div> */}
            <div className="applicantsupload">
              <div className="Applicantuploadfileicon">
                {!file && (
                  <img
                    src={process.env.PUBLIC_URL + '/assests/img/uploadBgIcon.svg'}
                    alt=""
                  />
                )}
              </div>

              <Button
                variant="contained"
                component="label"
                className="appuploadresume"
              >
                <span>
                  <b style={{ color: "var(--primary-bg-color)" }}>Upload</b>
                  <b style={{ color: "#9E9E9E" }}> browse file to upload </b>
                </span>
                <input
                  hidden
                  type="file"
                  ref={fileInputRef}
                  accept=".pdf,.docx,.txt,.doc"
                  onChange={handleFileChange}
                />
              </Button>

              <p className="docsupporttext">
                Supported file formats: Doc, Pdf, Docx
              </p>

              {file && (
                <ul className="filelistupload">
                  <li>
                    <p>{file.name}</p>
                    <span
                      className="closeImgbtn"
                      onClick={() => setFile(null)}
                    >
                      <DeleteOutlineIcon />
                    </span>
                  </li>
                </ul>
              )}

              {error && (
                <Typography color="error" variant="body2" sx={{ mt: 1, textAlign: "center" }}>
                  {error}
                </Typography>
              )}
            </div>

            {renderButtons("Parse", () => {
              if (!file) {
                setError("Please upload a file first.");
                dispatch(updateSnackbar({ type: "error", message: "Please upload a file first." }));
                return;
              }
              if (isAIEnabled) {
                handleParse(file, null);
              } else {
                handleClientSideParse(file, null);
              }
            }, isParsing)}
          </TabPanel>

          {/* Paste Text Tab */}
          <TabPanel value="2">
            {/* <div className="beta-warning">
                <WarningAmberIcon /><b>This is a beta release, please verify all results to ensure accuracy.</b>
              </div> */}
            <TextField
              multiline
              rows={10}
              fullWidth
              placeholder="Paste your job description text here..."
              value={pasteText}
              onChange={(e) => setPasteText(e.target.value)}
              disabled={isParsing}
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": {
                  padding: "16px",
                  fontFamily: "'Roboto', sans-serif",
                  fontSize: "14px",
                },
              }}
            />

            {error && (
              <Typography
                color="error"
                variant="body2"
                sx={{ mt: 1, textAlign: "center" }}
              >
                {error}
              </Typography>
            )}

            {renderButtons("Parse", handlePasteSubmit)}
          </TabPanel>

          {/* Create JD Tab (AI-only) */}
          {isAIEnabled && <TabPanel value="3" sx={{ textAlign: "left" }}>
            {/* <div className="beta-warning" style={{marginBottom: '20px'}}>
                <WarningAmberIcon /><b>This is a beta release, please verify all results to ensure accuracy.</b>
              </div> */}

            {jdTemplates.length > 0 && (
              <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
                <Box sx={{ width: 260 }}>
                  <FormControl fullWidth autoComplete="off">
                    <InputLabel shrink className="boldSelectlabel">
                      Choose a JD Template&nbsp;
                    </InputLabel>
                    <Select
                      value={selectedTemplateId}
                      name="jdTemplate"
                      label="Choose a JD Template"
                      autoComplete="off"
                      onChange={handleTemplatePick}
                      disabled={isParsing}
                      displayEmpty
                      sx={{
                        width: "100%",
                        height: "3.5rem",
                        borderRadius: "4px",
                        "& .MuiSelect-select": { padding: "16.5px 14px" },
                      }}
                    >
                      <MenuItem className="selectOptions" value="">
                        None
                      </MenuItem>
                      {jdTemplates.map((t) => (
                        <MenuItem className="selectOptions" key={t.id} value={t.id}>
                          {t.name || "Untitled"}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            )}

            {selectedTemplate ? (
              <React.Fragment>
                <div className="createjd-template">
                  {selectedTemplate.body && selectedTemplate.body.trim() ? (
                    hasSlots(selectedTemplate.body) ? (
                      selectedTemplate.body.split(/\n{2,}/).map((para, pIdx) => {
                        const parts = parseSlots(para);
                        if (parts.length === 0) return null;
                        return (
                          <p key={`tpl-p-${pIdx}`} className="createjd-template-paragraph">
                            {parts.map((p, i) => {
                              if (p.type === "text") {
                                const lines = p.value.split("\n");
                                return lines.map((line, li) => (
                                  <React.Fragment key={`tpl-t-${pIdx}-${i}-${li}`}>
                                    <span>{line}</span>
                                    {li < lines.length - 1 && <br />}
                                  </React.Fragment>
                                ));
                              }
                              return (
                                <TextField
                                  key={`tpl-i-${pIdx}-${i}`}
                                  variant="standard"
                                  placeholder={p.label}
                                  value={slotValues[p.label] || ""}
                                  onChange={handleSlotChange(p.label)}
                                  disabled={isParsing}
                                  sx={{ mx: 0.5, minWidth: 140, "& .MuiInput-root": { fontSize: "14px" } }}
                                />
                              );
                            })}
                          </p>
                        );
                      })
                    ) : (
                      <Typography sx={{ whiteSpace: "pre-wrap", fontSize: 14 }}>
                        {selectedTemplate.body}
                      </Typography>
                    )
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      This template has no content.
                    </Typography>
                  )}
                </div>

                <TextField
                  multiline
                  rows={3}
                  fullWidth
                  placeholder="Additional requirements or context (optional)"
                  value={templateInputs.additionalNotes}
                  onChange={handleTemplateChange("additionalNotes")}
                  disabled={isParsing}
                  variant="outlined"
                  sx={{
                    mt: 2,
                    "& .MuiOutlinedInput-root": {
                      padding: "16px",
                      fontFamily: "'Roboto', sans-serif",
                      fontSize: "14px",
                    },
                  }}
                />
              </React.Fragment>
            ) : (
            <React.Fragment>
            <div className="createjd-template">
              <span>We are looking for a </span>
              <TextField
                variant="standard"
                placeholder="Job Title"
                value={templateInputs.jobTitle}
                onChange={handleTemplateChange("jobTitle")}
                disabled={isParsing}
                sx={{ width: 180, mx: 0.5, "& .MuiInput-root": { fontSize: "14px" } }}
              />
              <span> with </span>
              <TextField
                variant="standard"
                placeholder="Min"
                value={templateInputs.minExperience}
                onChange={handleTemplateChange("minExperience")}
                disabled={isParsing}
                sx={{ width: 40, mx: 0.5, "& .MuiInput-root": { fontSize: "14px" } }}
                inputProps={{ inputMode: "numeric" }}
              />
              <span> to </span>
              <TextField
                variant="standard"
                placeholder="Max"
                value={templateInputs.maxExperience}
                onChange={handleTemplateChange("maxExperience")}
                disabled={isParsing}
                sx={{ width: 40, mx: 0.5, "& .MuiInput-root": { fontSize: "14px" } }}
                inputProps={{ inputMode: "numeric" }}
              />
              <span> years of experience. The ideal candidate should have expertise in </span>
              <TextField
                variant="standard"
                placeholder="Primary Skills (comma-separated)"
                value={templateInputs.primarySkills}
                onChange={handleTemplateChange("primarySkills")}
                disabled={isParsing}
                sx={{ width: 260, mx: 0.5, "& .MuiInput-root": { fontSize: "14px" } }}
              />
              <span> and familiarity with </span>
              <TextField
                variant="standard"
                placeholder="Secondary Skills"
                value={templateInputs.secondarySkills}
                onChange={handleTemplateChange("secondarySkills")}
                disabled={isParsing}
                sx={{ width: 200, mx: 0.5, "& .MuiInput-root": { fontSize: "14px" } }}
              />
              <span>. This is a </span>
              <select
                value={templateInputs.jobType}
                onChange={handleTemplateChange("jobType")}
                disabled={isParsing}
                className="createjd-inline-select"
              >
                <option value="">Job Type</option>
                <option value="Full time">Full time</option>
                <option value="Part time">Part time</option>
                <option value="Intern">Intern</option>
                <option value="Contract">Contract</option>
              </select>
              <span> </span>
              <select
                value={templateInputs.workModel}
                onChange={handleTemplateChange("workModel")}
                disabled={isParsing}
                className="createjd-inline-select"
              >
                <option value="">Work Model</option>
                <option value="Remote">Remote</option>
                <option value="On-site">On-site</option>
                <option value="Hybrid">Hybrid</option>
              </select>
              <span> position based in </span>
              <TextField
                variant="standard"
                placeholder="Location"
                value={templateInputs.location}
                onChange={handleTemplateChange("location")}
                disabled={isParsing}
                sx={{ width: 160, mx: 0.5, "& .MuiInput-root": { fontSize: "14px" } }}
              />
              <span>. </span>
              <TextField
                variant="standard"
                placeholder="Education requirement"
                value={templateInputs.education}
                onChange={handleTemplateChange("education")}
                disabled={isParsing}
                sx={{ width: 200, mx: 0.5, "& .MuiInput-root": { fontSize: "14px" } }}
              />
              <span> or equivalent is preferred. The role is in the </span>
              <TextField
                variant="standard"
                placeholder="Industry / Domain"
                value={templateInputs.industry}
                onChange={handleTemplateChange("industry")}
                disabled={isParsing}
                sx={{ width: 150, mx: 0.5, "& .MuiInput-root": { fontSize: "14px" } }}
              />
              <span> domain at </span>
              <TextField
                variant="standard"
                placeholder="Company Name"
                value={templateInputs.companyName}
                onChange={handleTemplateChange("companyName")}
                disabled={isParsing}
                sx={{ width: 160, mx: 0.5, "& .MuiInput-root": { fontSize: "14px" } }}
              />
              <span>.</span>
            </div>

            <TextField
              multiline
              rows={3}
              fullWidth
              placeholder="Additional requirements or context (optional)"
              value={templateInputs.additionalNotes}
              onChange={handleTemplateChange("additionalNotes")}
              disabled={isParsing}
              variant="outlined"
              sx={{
                mt: 2,
                "& .MuiOutlinedInput-root": {
                  padding: "16px",
                  fontFamily: "'Roboto', sans-serif",
                  fontSize: "14px",
                },
              }}
            />
            </React.Fragment>
            )}

            {error && (
              <Typography
                color="error"
                variant="body2"
                sx={{ mt: 1, textAlign: "center" }}
              >
                {error}
              </Typography>
            )}

            {renderButtons("Create JD", selectedTemplate ? handleCreateJDFromTemplate : handleCreateJD)}
          </TabPanel>}
        </TabContext>
      </Box>
    </div>
  );
};

export default UploadJDPanel;
